import { ComponentProps } from "react";
import NavItem from "src/components/layout/LayoutCompany/sidebar/NavItem";
import SubscribeExternCard from "src/components/promotion/cards/SubscribeExternCard";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useAuth from "src/hooks/selectors/useAuth";
import useSubscription from "src/hooks/selectors/useSubscription";
import useLayout from "src/hooks/useLayout";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import { cn } from "src/lib/utils";
import BoardNavItems from "src/components/layout/LayoutCompany/sidebar/BoardNavItems";
import usePermissions from "src/hooks/api/services/auth/usePermissions";
import SubscribeInternCard from "src/components/promotion/cards/SubscribeInternCard";

const NavItems = ({ t }: Translation) => {
	const auth = useAuth();
	const {
		tenant: { slug, modules, urls },
	} = useTenant();
	const subscription = useSubscription();
	const permissions = usePermissions();

	if (
		auth.type === "student" &&
		!modules.includes("intern-task") &&
		modules.includes("extern-task")
	) {
		return (
			<>
				<Wrapper className="flex-1">
					{permissions.has("registrations.create") && (
						<NavItem
							disabled={!auth.is_verified}
							help={
								!auth.is_verified
									? t("help.search_tasks")
									: undefined
							}
							to={
								slug === "getcrewdone"
									? urls.public + "/evenementen"
									: urls.public + "/opdrachten/alles/overal"
							}
							className="bg-primary justify-center items-center mb-4 hover:border-primary text-primary-foreground"
							icon={{
								className:
									"bg-primary group-hover:bg-primary text-primary-foreground",
								font_awesome: {
									name: "fas fa-search",
								},
							}}
						>
							{t("search-new-tasks")}
						</NavItem>
					)}
					<NavItem icon="fas fa-user" to="/dashboard">
						{t("my-profile")}
					</NavItem>
					<NavItem icon="fa-check-circle" to="/tasks">
						{t("my-tasks")}
					</NavItem>
					{modules.includes("talentboard") && (
						<NavItem
							icon="fas fa-list"
							to="/offers"
							count={auth?.unreads?.offers}
						>
							{t("my-offers")}
						</NavItem>
					)}
					<NavItem
						icon="fas fa-comments"
						to="/inbox"
						count={auth?.unreads?.messages}
					>
						{t("messages")}
					</NavItem>
					{modules.includes("student-pool") && (
						<NavItem
							icon="fas fa-users"
							to="/network"
							count={auth?.unread_invites}
						>
							{t("my-network")}
						</NavItem>
					)}
					{modules.includes("hour-validation") && (
						<NavItem
							to="/hours"
							icon="fas fa-alarm-clock"
							count={auth?.unreads?.hours}
						>
							{t("my-hours")}
						</NavItem>
					)}
					<NavItem icon="fas fa-credit-card" to="/payments">
						{t("my-payments")}
					</NavItem>
					<NavItem icon="fas fa-file-signature" to="/envelopes">
						{t("my-envelopes")}
					</NavItem>
				</Wrapper>
			</>
		);
	}

	return (
		<>
			<Wrapper className="flex-1">
				<NavItem
					className="gtm-company-dashboard-sidebar--link-dashboard"
					to="/dashboard"
					icon="fa-home"
				>
					{t("dashboard")}
				</NavItem>

				{modules.includes("intern-task") && auth.type === "company" && (
					<NavItem
						className="gtm-company-dashboard-sidebar--link-projects"
						to="/projects"
						icon="fa-folder"
					>
						{t("projects")}
					</NavItem>
				)}

				<hr className="border-border my-4 lg:hidden xl:flex" />

				{modules.includes("intern-task") ? (
					<BoardNavItems />
				) : (
					<NavItem icon="fa-check-circle" to="/tasks">
						{t("tasks")}
					</NavItem>
				)}

				{modules.includes("extern-task") && (
					<>
						<NavItem
							to="/inbox"
							count={auth?.unreads?.messages}
							icon="fa-comments"
						>
							{t("messages")}
						</NavItem>
						{modules.includes("student-pool") && (
							<NavItem
								className="gtm-company-dashboard-sidebar--link-studentenpool"
								to="/network"
								icon="fa-users"
							>
								{t("studentpool")}
							</NavItem>
						)}
						{modules.includes("talentboard") &&
							auth.type === "company" && (
								<NavItem
									to="/talents"
									count={auth?.unreads?.offers}
									icon="fa-users-crown"
								>
									{t("talents")}
								</NavItem>
							)}
					</>
				)}
			</Wrapper>
			<Wrapper>
				{!subscription?.id && (
					<>
						{modules.includes("extern-task") && (
							<SubscribeExternCard />
						)}

						{modules.includes("intern-task") &&
							!modules.includes("extern-task") && (
								<SubscribeInternCard />
							)}
					</>
				)}
				<NavItem to="/feedback" icon="fa-comment-alt">
					{t("feedback")}
				</NavItem>

				{modules.includes("hour-validation") && (
					<NavItem
						className="gtm-company-dashboard-sidebar--link-studentenpool"
						to="/hours"
						icon="fa-alarm-clock"
						count={auth?.unreads?.hours}
					>
						{t("hours")}
					</NavItem>
				)}

				{permissions.has("analytics.view") &&
					modules.includes("analytics") && (
						<NavItem icon="fa-chart-line" to="/analytics/dashboard">
							{t("analytics")}
						</NavItem>
					)}

				{modules.includes("extern-task") && (
					<>
						<NavItem
							className="gtm-company-dashboard-sidebar--link-payments"
							key="gtm-company-dashboard-sidebar--link-payments"
							to="/payments"
							count={auth?.unreads?.open_payments}
							icon="fa-credit-card"
						>
							{t("payments")}
						</NavItem>
						<NavItem
							key="gtm-company-dashboard-sidebar--link-envelopes"
							className="gtm-company-dashboard-sidebar--link-envelopes"
							to="/envelopes"
							count={auth?.unreads?.envelopes}
							icon="fa-file-signature"
						>
							{t("agreements")}
						</NavItem>
					</>
				)}
				{slug === "odylyt" && (
					<NavItem
						icon="fas fa-question"
						to={`mailto:support@odylyt.com?body=${encodeURIComponent(
							`\n\nCode: ${auth.hid}\nOrigin: ${window.location.href}`
						)}`}
					>
						Support
					</NavItem>
				)}
			</Wrapper>
		</>
	);
};

const Wrapper = ({ children, ...rest }: ComponentProps<"div">) => {
	const { layout } = useLayout();
	return (
		<div
			className={cn(
				"flex flex-col p-4 gap-2",
				layout.navbar.variant === "default" &&
					"xl:min-w-[272px] w-full min-w-auto",
				rest?.className
			)}
		>
			{children}
		</div>
	);
};

NavItems.locale = {
	nl: {
		section: {
			tasks: "Taken",
			"my-boards": "Mijn takenborden",
			general: "Algemeen",
		},
		help: {
			search_tasks: "Maak je account compleet",
		},
		"search-new-tasks": "Opdrachten zoeken",
		dashboard: "Mijn dashboard",
		messages: "Berichten",
		hours: "Uren controle",
		"my-hours": "Urenregistratie",
		studentpool: "Connecties",
		talents: "Professionals",
		payments: "Betalingen",
		agreements: "Overeenkomsten",
		"my-profile": "Mijn profiel",
		signout: "Uitloggen",
		tasks: "Opdrachten",
		"search-tasks": "Opdrachten zoeken",
		"my-tasks": "Mijn opdrachten",
		"my-offers": "Mijn aanbod",
		"my-network": "Mijn netwerk",
		"my-payments": "Mijn betalingen",
		"my-envelopes": "Mijn overeenkomsten",
		links: "Links",
		faq: "Veelgestelde vragen",
		contact: "Contact",
		"edit-profile": "Profiel aanpassen",
		settings: "Instellingen",
		reviews: "Reviews",
		feedback: "Feedback",
		tutorial: "Tutorial",
		community: "Community",
		administration: "Administratie",
		invite: "Referrals",
		projects: "Projecten",
		analytics: "Analytics",
	},
	en: {
		section: {
			tasks: "Tasks",
		},
		help: {
			search_tasks: "Complete your account",
		},
		"search-new-tasks": "Search tasks",
		dashboard: "My dashboard",
		messages: "Messages",
		hours: "Hour validation",
		"my-hours": "Hour registration",
		studentpool: "Connections",
		talents: "Professionals",
		payments: "Payments",
		agreements: "Agreements",
		"my-profile": "My Profile",
		signout: "Sign Out",
		tasks: "Tasks",
		"search-tasks": "Search Tasks",
		"my-tasks": "My Tasks",
		"my-offers": "My Offers",
		"my-network": "My Network",
		"my-payments": "My Payments",
		"my-envelopes": "My agreements",
		links: "Links",
		faq: "FAQ",
		contact: "Contact",
		"edit-profile": "Edit Profile",
		settings: "Settings",
		reviews: "Reviews",
		feedback: "Feedback",
		tutorial: "Tutorial",
		community: "Community",
		administration: "Administration",
		invite: "Referrals",
		projects: "Projects",
		analytics: "Analytics",
	},
};

export default withTranslation(NavItems);
